import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { seededName } from '@/shared/lib/seededName'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { type Money } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { REASON_MAX } from './hrApi'
import { setSalaryItem, type PayComponent } from './payApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  open: boolean
  personId: string
  /** ⚠️ FAQAT takrorlanadigan va faol qismlar — tanlov sababi pastda */
  components: readonly PayComponent[]
  onClose: () => void
}

interface FormValues {
  componentId: string
  amount: Money | null
  validFrom: Dayjs
  reason: string
}

/**
 * Doimiy qism belgilash — oklad, ustama, doimiy ushlanma.
 *
 * <h2>⚠️ TANLOVDA FAQAT `recurring` QISMLAR</h2>
 * Bazadagi `hr.pay_item_guard()` bir martalik qismga doimiy davr
 * yozishni RAD ETADI. Ro'yxatga ularni ham qo'shish operatorga
 * tanlatib, keyin tushunarsiz 400 ko'rsatardi — shuning uchun tanlov
 * chegarasi shu yerda.
 *
 * <h2>⚠️ SUMMA HAR DOIM MUSBAT</h2>
 * `DEDUCTION` ham musbat kiritiladi: ishorani QISM TURI beradi
 * (`PayComponentKind`). Manfiy kiritish yo'li ochilsa, «-50 000 li
 * ushlanma» qo'shilib ketardi va nuqson faqat vedomost jamida
 * ko'rinardi. Server ham nol va manfiyni rad etadi.
 *
 * <h2>⚠️ ESKI DAVR AVTOMAT YOPILADI</h2>
 * Yangi qator o'sha qism bo'yicha ochiq davrni yopadi — bu servisning
 * ishi, panel ikkinchi so'rov yubormaydi. Tarix esa o'zgarmaydi
 * (`hr.temporal_row_immutable()`), ya'ni eski summa joyida qoladi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * `onError` YO'Q: 400 («qism takrorlanmaydigan») va 409 aynan shu
 * oynada o'qilishi kerak. Yopishda `save.reset()`.
 */
export function SalaryItemModal({ open, personId, components, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      componentId: undefined,
      amount: null,
      validFrom: tenantToday(),
      reason: '',
    })
  }, [open, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      setSalaryItem(personId, {
        componentId: values.componentId,
        // ⚠️ `MoneyInput` MINOR birlikda beradi — bu yerda hech narsa
        // ko'paytirilmaydi (`money.ts`: aylantirish faqat bitta joyda)
        amountMinor: values.amount?.amount ?? 0,
        validFrom: values.validFrom.format(DATE_FORMAT),
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      // ⚠️ `hr` PREFIKSI: yangi qism keyingi vedomostga tushadi, ya'ni
      // vedomost ro'yxati ham eskiradi
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.all })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.salary.setTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      okButtonProps={{ disabled: components.length === 0 }}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.salary.setNote')} fontSize={13} />
      </div>

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      {components.length === 0 ? (
        <Alert type="warning" showIcon message={t('hr.salary.noComponents')} />
      ) : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
          <Form.Item
            name="componentId"
            label={t('hr.salary.component')}
            extra={t('hr.salary.componentHint')}
            rules={[{ required: true, message: t('hr.salary.validation.componentRequired') }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('hr.salary.componentPlaceholder')}
              options={components.map((component) => ({
                value: component.id,
                label: `${seededName('payComponents', component.code, component.name, t)} · ${t(`payComponentKind.${component.kind}`)}`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="amount"
            label={t('hr.salary.amount')}
            extra={t('hr.salary.amountHint')}
            rules={[
              {
                validator: (_rule, value: Money | null) =>
                  value && value.amount > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('hr.salary.validation.amountRequired'))),
              },
            ]}
          >
            <MoneyInput autoFocus />
          </Form.Item>

          <Form.Item
            name="validFrom"
            label={t('hr.salary.validFrom')}
            extra={t('hr.salary.validFromHint')}
            rules={[{ required: true, message: t('hr.salary.validation.validFromRequired') }]}
          >
            <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="reason"
            label={t('hr.salary.reason')}
            extra={t('hr.salary.reasonHint')}
            rules={[
              { required: true, message: t('hr.salary.validation.reasonRequired') },
              { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
            ]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
